import { useState, useEffect } from "react";
import { useNavigate } from "react-router";
import { useAuth } from "@/hooks/useAuth";
import { TISACard } from "@/components/tisa/TISACard";
import { TISAButton } from "@/components/tisa/TISAButton";
import { TISAPinInput } from "@/components/tisa/TISAInput";
import { getAgeAppropriateGreeting, playSuccessSound } from "@/lib/utils";
import { toast } from "@/hooks/use-toast";
import { supabase } from "@/lib/supabase";
import { ChevronLeft } from "lucide-react";

interface Student {
  id: string;
  name: string;
  class_id: number | null;
}

interface StudentLoginFormProps {
  onBack: () => void;
}

type LoginStep = "class" | "student" | "pin";

// Grade configuration with emojis - class_id is the integer key
const GRADES = [
  { class_id: 0, emoji: "🌱", label: "Grade 0" },
  { class_id: 1, emoji: "🌿", label: "Grade 1" },
  { class_id: 2, emoji: "🌳", label: "Grade 2" },
  { class_id: 3, emoji: "🌟", label: "Grade 3" },
  { class_id: 4, emoji: "⭐", label: "Grade 4" },
  { class_id: 5, emoji: "✨", label: "Grade 5" },
  { class_id: 6, emoji: "🎓", label: "Grade 6" },
  { class_id: 7, emoji: "🏆", label: "Grade 7" },
  { class_id: 8, emoji: "🚀", label: "Grade 8" },
];

export function StudentLoginForm({ onBack }: StudentLoginFormProps) {
  // State management
  const [step, setStep] = useState<LoginStep>("class");

  // Data state
  const [selectedClassId, setSelectedClassId] = useState<number | null>(null);
  const [selectedStudent, setSelectedStudent] = useState<Student | null>(null);
  const [students, setStudents] = useState<Student[]>([]);
  const [pin, setPin] = useState("");

  // Loading states
  const [loadingStudents, setLoadingStudents] = useState(false);
  const [loading, setLoading] = useState(false);

  const { signInWithPin } = useAuth();
  const navigate = useNavigate();

  // Load students when a class is selected
  useEffect(() => {
    if (selectedClassId !== null && step === "student") {
      loadStudents();
    }
  }, [selectedClassId, step]);

  const loadStudents = async () => {
    if (selectedClassId === null) return;

    setLoadingStudents(true);
    try {
      const { data, error } = await supabase
        .from("children")
        .select("id, first_name, last_name, class_id")
        .eq("role", "student")
        .eq("class_id", selectedClassId)
        .order("first_name");

      if (error) throw error;

      // NO FALLBACK - if no students, show error. Don't hide with empty array.
      if (!data || data.length === 0) {
        throw new Error(
          `No students found in Grade ${selectedClassId}. Check database for student records.`,
        );
      }

      // Map to expected Student interface with combined name
      const mappedStudents: Student[] = data.map((child) => ({
        id: child.id,
        name: `${child.first_name} ${child.last_name}`,
        class_id: child.class_id,
      }));

      setStudents(mappedStudents);
    } catch (error) {
      const err =
        error instanceof Error ? error : new Error("Failed to load students");
      console.error("Error loading students:", err);
      toast({
        title: "Oops! 🤔",
        description:
          err.message || "Could not load students. Please try again.",
        variant: "destructive",
      });
      setStudents([]);
    } finally {
      setLoadingStudents(false);
    }
  };

  const handleClassSelect = (classId: number) => {
    setSelectedClassId(classId);
    setStep("student");
  };

  const handleStudentSelect = (student: Student) => {
    setSelectedStudent(student);
    setStep("pin");
  };

  const handleStudentLogin = async () => {
    if (pin.length !== 4) {
      toast({
        title: "Oops! 🤔",
        description: "Please enter your 4-digit PIN",
        variant: "destructive",
      });
      return;
    }

    if (!selectedStudent) {
      toast({
        title: "Error",
        description: "No student selected",
        variant: "destructive",
      });
      return;
    }

    setLoading(true);
    try {
      // Pass selectedStudent.id to disambiguate if multiple students have same PIN
      const user = await signInWithPin(pin, selectedStudent.id);

      playSuccessSound();
      toast({
        title: getAgeAppropriateGreeting(user.name),
        description: `You have ${user.coins} TISAcoins! 🪙`,
      });

      // Navigate to student dashboard
      navigate("/student/dashboard");
    } catch (error) {
      toast({
        title: "Hmm, that doesn't look right 🤔",
        description:
          error instanceof Error
            ? error.message
            : "Please check your PIN and try again",
        variant: "destructive",
      });
      setPin(""); // Clear PIN on error
    } finally {
      setLoading(false);
    }
  };

  const handleBackFromStudent = () => {
    setSelectedClassId(null);
    setStudents([]);
    setStep("class");
  };

  const handleBackFromPin = () => {
    setSelectedStudent(null);
    setPin("");
    setStep("student");
  };

  return (
    <TISACard size="lg" className="w-full max-w-2xl">
      <div className="space-y-6">
        {/* Header */}
        <div className="text-center">
          <div className="text-5xl mb-3">🎒</div>
          <h2 className="text-child-2xl font-bold text-tisa-blue-600 mb-2">
            Student Login
          </h2>
          <p className="text-child-base text-muted-foreground">
            {step === "class" && "Select your class"}
            {step === "student" &&
              `Find your name in Grade ${selectedClassId}`}
            {step === "pin" && "Enter your PIN"}
          </p>
        </div>

        {/* Step 1: Class Selection */}
        {step === "class" && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {GRADES.map((gradeInfo) => (
                <button
                  key={gradeInfo.class_id}
                  onClick={() => handleClassSelect(gradeInfo.class_id)}
                  className="bg-white border-4 border-tisa-blue-100 rounded-xl p-6 hover:border-tisa-blue-400 hover:bg-tisa-blue-50 hover:scale-105 transition-all duration-200 cursor-pointer group"
                >
                  <div className="text-4xl mb-2 group-hover:scale-110 transition-transform">
                    {gradeInfo.emoji}
                  </div>
                  <div className="text-child-lg font-bold text-tisa-blue-600">
                    {gradeInfo.label}
                  </div>
                </button>
              ))}
            </div>

            <TISAButton
              onClick={onBack}
              variant="outline"
              size="md"
              className="w-full"
              icon={<ChevronLeft className="w-4 h-4" />}
            >
              Back to Login Options
            </TISAButton>
          </div>
        )}

        {/* Step 2: Student Selection */}
        {step === "student" && (
          <div className="space-y-4">
            {loadingStudents ? (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-tisa-blue-600 mx-auto"></div>
                <p className="mt-4 text-muted-foreground">
                  Loading students...
                </p>
              </div>
            ) : students.length === 0 ? (
              <div className="text-center py-8">
                <div className="text-4xl mb-2">🤷</div>
                <p className="text-child-base text-muted-foreground">
                  No students found in Grade {selectedClassId}
                </p>
              </div>
            ) : (
              <div className="flex justify-center">
                <div className="w-full max-w-sm">
                  {students.map((student) => (
                    <button
                      key={student.id}
                      onClick={() => handleStudentSelect(student)}
                      className="w-full bg-gradient-to-br from-purple-50 to-pink-50 border-4 border-purple-200 rounded-xl p-6 hover:border-purple-400 hover:from-purple-100 hover:to-pink-100 hover:scale-105 hover:shadow-xl transition-all duration-200 cursor-pointer"
                    >
                      <div className="font-black text-child-2xl text-purple-600 mb-2">
                        {student.name}
                      </div>
                      <div className="text-child-base text-purple-500 flex items-center justify-center gap-2 font-semibold">
                        <span className="text-2xl">📘</span>
                        <span>Grade {student.class_id}</span>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}

            <TISAButton
              onClick={handleBackFromStudent}
              variant="outline"
              size="md"
              className="w-full"
              icon={<ChevronLeft className="w-4 h-4" />}
            >
              Back to Class Selection
            </TISAButton>
          </div>
        )}

        {/* Step 3: PIN Input */}
        {step === "pin" && selectedStudent && (
          <div className="space-y-6">
            {/* Student Confirmation Card */}
            <div className="bg-gradient-to-br from-tisa-blue-50 to-tisa-purple-50 border-2 border-tisa-blue-200 rounded-lg p-4 text-center">
              <p className="text-child-sm text-muted-foreground mb-1">
                Logging in as:
              </p>
              <p className="text-child-xl font-bold text-tisa-blue-600">
                {selectedStudent.name}
              </p>
              <div className="flex items-center justify-center gap-1 mt-1">
                <span>📘</span>
                <p className="text-child-sm text-muted-foreground">
                  Grade {selectedStudent.class_id}
                </p>
              </div>
            </div>

            {/* PIN Input */}
            <TISAPinInput
              value={pin}
              onChange={setPin}
              error={pin.length === 4 && false}
              disabled={loading}
            />

            {/* Action Buttons */}
            <div className="space-y-3">
              <TISAButton
                onClick={handleStudentLogin}
                variant="theme-blue"
                size="lg"
                className="w-full"
                isLoading={loading}
                disabled={pin.length !== 4}
              >
                {loading ? "Signing In..." : "Enter TISAverse! 🚀"}
              </TISAButton>

              <TISAButton
                onClick={handleBackFromPin}
                variant="outline"
                size="md"
                className="w-full"
                disabled={loading}
                icon={<ChevronLeft className="w-4 h-4" />}
              >
                Choose Different Student
              </TISAButton>
            </div>

            {/* Help Text */}
            <div className="text-center">
              <p className="text-child-xs text-muted-foreground">
                Forgot your PIN? Ask your teacher! 🤚
              </p>
            </div>
          </div>
        )}
      </div>
    </TISACard>
  );
}
